import { cloneElement, type ReactElement, type ReactNode } from 'react';
import {
  List,
  Datagrid,
  FunctionField,
  TextInput,
  SelectInput,
  BooleanInput,
  DateInput,
  RaRecord,
  type ListProps,
} from 'react-admin';
import { planningStatusChoices } from './planningStatus';
import { useStatuses } from './statusDictionary';
import {
  NumberWithSourceCell,
  CompactRouteCell,
  PlanningCell,
  TmsCell,
  FlagsCell,
  CompactContractorCell,
  CompanyCodeCell,
  CompactVehicleCell,
  VatCell,
} from './listCells';
import { ExchangeCell } from './cells';
import { FORKLIFT_FILTER_DEFAULT } from './forklift';
import { MARKERS_FILTER_CHOICES, markersFilterChoices, saleFilterChoices } from './markers';
import { MyOrdersToggle } from './MyOrdersToggle';
import { PermissionGate, useHasPermission } from '../PermissionGate';
import { ELEMENT } from '../roleCenter/elementKeys';
import { FeaturesLoading, useFeature, useFeaturesLoading } from '../featureFlags/FeatureGate';
import { TMS_ORDER_STATUS_CHOICES } from './tmsOrderStatus';
import { SOURCE_CHOICES } from './recordSource';
import { Stack } from '@mui/material';
import { SyncStatus } from '../SyncStatus';
import { menuLabel } from '../menuConfig';
import { ListExportActions } from '../../shared/ListExportActions';
import { bankExporterFor } from './exportColumns';
import {
  FEATURE_EXCHANGE_COLUMN,
  FEATURE_POSTED_ON_EXCHANGE_FILTER,
  FEATURE_SALE_RECOMMENDATION,
} from './listFeatures';
import { ClientDayField } from '../../shared/DateFields';
import { PricePerKmCell } from './PriceStatisticsLine';
import { FEATURE_PRICE_STATISTICS } from './priceStatistics';
import {
  CARETAKER_FILTER,
  COUNTRY_CHOICES,
  FEATURE_BANK_COLUMNS,
  FEATURE_BANK_FILTERS,
  FEATURE_BANK_RATE_TYPE_COLUMNS,
  LOADING_WITHIN_CHOICES,
  MY_FLEET_TITLE,
  REQUIREMENT_CHOICES,
  VEHICLE_TYPE_CHOICES,
  withWithoutVariantChoice,
} from './bankFtl';
import { CaretakerFilter, PlanningStatusMultiFilter } from './BankFtlFilters';
import { FEATURE_VARIANT_VALIDITY, withVariantMarkerChoices } from './variantValidity';
import {
  FEATURE_LOADING_TOMORROW_ALERT,
  FEATURE_PLANNER_DASHBOARD,
  FEATURE_TEAM_LEADER_DASHBOARD,
  MY_VARIANTS_CHOICES,
  UNASSIGN_REQUEST_CHOICES,
  unassignRequestChoices,
  withFtlDashboardFilters,
  withWithVariantChoice,
} from './ftlDashboardFilters';
import {
  FEATURE_TRADE_TRANSFER_BEFORE_DEPARTURE,
  FEATURE_VEHICLE_CHANGE_REQUEST,
} from './changeRequests';
import {
  ContractorNarrowCell,
  ContractorWithRateCell,
  LoadingWindowCell,
  RateCell,
  RouteWithPointsCell,
  RouteWithRequirementsCell,
  VehicleOrVariantsCell,
  VehicleTypeCell,
} from './BankFtlCells';
import { BankViewsMenu } from './BankViewsMenu';
import {
  EXECUTION_DEPARTMENT_CHOICES,
  FEATURE_EXECUTION_HINT,
} from '../executionDepartment/executionDepartmentApi';
import {
  FEATURE_PRE_ORDER_STAGE,
  PRE_ORDER_STAGE_CHOICES,
  PRE_ORDER_STAGE_CHOICES_TMS_EXPORT,
} from './preOrderStage';
import { FEATURE_TMS_EXPORT_ONE_STEP } from '../../shared/status';
import {
  FEATURE_TRADE_TRANSFER_REASONS,
  TRADE_TRANSFER_KIND_LABELS,
  isTradeTransferKind,
  useTradeReasons,
  type TradeTransferReason,
} from '../tradeReasons/tradeReasonsApi';
import { FEATURE_CARRIER_ACCEPTANCE, FEATURE_CARRIER_OFFERS } from './carrierOffersApi';
import { FEATURE_TRADE_DASHBOARD } from '../tradeDashboard/tradeDashboardApi';

/**
 * Bank zleceń — lista.
 *
 * Rozdział FTL §4: *„Bank zleceń ma agregować zlecenia **i pre-zlecenia**
 * z TMS, Transporeon, maili oraz wcześniejszych etapów obsługi"*. Domyślny
 * widok pokazuje stany żywe, czyli **razem z prezleceniami**; archiwum
 * i rekordy zamknięte przez Trade wymagają jawnego filtra.
 *
 * ## Trzy osie obok siebie, celowo
 *
 * Kolumny „Stan planowania" i „TMS" to **dwie różne osie**, a ryzyko jest
 * trzecią i pokazuje się jako znacznik przy stanie. Zlecenie obsadzone
 * i zagrożone widać jako obsadzone **z ostrzeżeniem** — nie jako sam alert.
 *
 * Same komórki siedzą w `cells.tsx` i `listCells.tsx`, bo nie potrzebują react-admina i dzięki
 * temu dają się testować bez całej ramy.
 */

/**
 * Widoki FTL i Trade to DWA FILTRY nad jednym bankiem, nie dwie listy.
 * Które stany należą do którego działu, mówi słownik po stronie API
 * (`OrderStatus.department`) — front wysyła tylko nazwę działu. Jeden rekord jest
 * w jednym stanie, więc trafia do dokładnie jednego widoku (albo do obu,
 * gdy stan działu nie ma — archiwum jest wspólne). Bez duplikacji.
 */
// „Trade — przewoźnicy i giełdy” (poprawa brzmienia 01.10.2026): samo
// „sprzedaż” myliło się ze sprzedażą usług zleceniodawcom. Wartości FTL /
// TRADE to klucze filtra — bez zmian.
export const DEPARTMENT_CHOICES = [
  { id: 'FTL', name: 'FTL — flota własna' },
  { id: 'TRADE', name: 'Trade — przewoźnicy i giełdy' },
];

/**
 * Wybór stanu ze słownika klienta (etykiety, stany dodane wierszem); zanim
 * słownik przyjdzie — kopia z kodu. Osobny komponent, bo tablica filtrów
 * react-admina jest statyczna, a lista przychodzi z API.
 */
const PlanningStatusFilter = (props: {
  source: string;
  label: string;
  alwaysOn?: boolean;
}) => {
  const states = useStatuses();
  return <SelectInput {...props} choices={planningStatusChoices(states)} />;
};

/**
 * Filtr „Znaczniki” — opcje z elementu, bez „Z alertem przeplanowania” dla
 * kogoś bez uprawnienia „Obsadzanie pojazdem” (`FTL_ASSIGN`; decyzja
 * użytkownika 12.10.2026: „alert przeplanowania — opcja: widzi tylko
 * master”) — API i tak oddaje mu na tym filtrze pustą listę. Osobny
 * komponent z tego samego powodu co wybór stanu: tablica filtrów jest
 * statyczna, a uprawnienie przychodzi z hooka.
 */
const MarkersFilter = ({
  choices,
  ...props
}: {
  source: string;
  label: string;
  alwaysOn?: boolean;
  sx?: typeof MARKERS_FILTER_SX;
  choices: typeof MARKERS_FILTER_CHOICES;
}) => {
  const hasAssign = useHasPermission('FTL_ASSIGN');
  // „Tylko master” to przełącznik funkcji (12.10.2026) — wyłączony: opcja
  // przeplanowania dla każdego, jak na produkcji.
  const masterOnly = useFeature('replanAlertMasterOnly');
  // „Do sprzedaży” tylko z przełącznikiem (12.10.2026, `listFeatures.ts`).
  const saleOn = useFeature(FEATURE_SALE_RECOMMENDATION);
  // „Bez wariantu” — filtry z makiety FTL (15.10.2026, `bankFtl.ts`).
  const bankFiltersOn = useFeature(FEATURE_BANK_FILTERS);
  // Konflikt wariantów w czasie i warianty po terminie (20.10.2026).
  const variantValidityOn = useFeature(FEATURE_VARIANT_VALIDITY);
  // „Z wariantem” — pulpit Team Leadera (20.10.2026, `ftlDashboardFilters.ts`).
  const withVariantOn = useFeature(FEATURE_TEAM_LEADER_DASHBOARD);
  return (
    <SelectInput
      {...props}
      choices={withWithVariantChoice(
        withVariantMarkerChoices(
          withWithoutVariantChoice(
            saleFilterChoices(markersFilterChoices(choices, !masterOnly || hasAssign), saleOn),
            bankFiltersOn,
          ),
          variantValidityOn,
        ),
        withVariantOn,
      )}
    />
  );
};

/**
 * Filtr „Znaczniki” nie daje się ścisnąć. Wiersz filtrów to flex
 * z zawijaniem, a pole wyboru z wielokropkiem ma prawie zerową szerokość
 * minimalną — gdy „Stan planowania” dostawał wartość, pole kurczyło się do
 * „Czynności wózk…” przy 1366 px (weryfikacja na dev, 28.09.2026). Z tą
 * szerokością mieści się najdłuższa wartość („Z alertem przeplanowania”,
 * dłuższa od dawnej „Ukryj czynności wózkowe”), a brak miejsca przenosi
 * pole do następnej linii.
 */
export const MARKERS_FILTER_SX = { minWidth: 250, flexShrink: 0 } as const;

// Etykiety filtrów — poprawa brzmienia 01.10.2026. Szukajka ma krótką
// etykietę, bo dłuższa ucinała się przy 1366 px; „referencja” była
// niejasna, a numer referencyjny i tak łapie się pod „Numer”. Klucze
// (`source`) bez zmian — react-admin pamięta je użytkownikom.
const listFilters = [
  // Kontrakt `data-testid` paczki (pomiar użycia, S-22.10-02): pole szukania banku.
  <TextInput source="q" label="Numer, miasto, kod" alwaysOn slotProps={{ htmlInput: { 'data-testid': 'ftl-bank-szukaj' } }} />,
  <SelectInput
    source="dzial"
    label="Widok działu"
    choices={DEPARTMENT_CHOICES}
    alwaysOn
  />,
  <PlanningStatusFilter source="planningStatus" label="Stan planowania" alwaysOn />,
  // „Znaczniki” (omówienie 01.10.2026; wcześniej „Czynności wózkowe”).
  // Klucz `forklift` i wartości hide/only/all zostają — react-admin
  // zapamiętał je użytkownikom. Czynność wózkowa to osobny rekord TMS przy
  // zleceniu głównym, nie transport — domyślnie jej nie ma na liście. Pusty
  // wybór API też traktuje jak „ukryj”, więc wyczyszczenie filtra ich nie
  // wysypie na listę. „Z alertem przeplanowania” to nowa wartość tego
  // samego klucza (`replan`); czynności wózkowe są wtedy ukryte. Tak samo
  // „Straciły pojazd” (`lostVehicle`) — czerwona lista z omówienia 01.10.2026.
  <MarkersFilter
    source="forklift"
    label="Znaczniki"
    choices={MARKERS_FILTER_CHOICES}
    alwaysOn
    sx={MARKERS_FILTER_SX}
  />,
  // `defaultValue`: przełącznik dodany z „Dodaj filtr” od razu filtruje.
  // Bez tego pojawiał się wyłączony i trzeba go było przełączyć, żeby
  // zadziałał (e2e, 28.09.2026) — react-admin bierze wartość nowo
  // pokazanego filtra z `defaultValue` elementu.
  <BooleanInput source="atRisk" label="Tylko zagrożone" defaultValue />,
  // „Po terminie ważności”, nie samo „po terminie” — to nie termin załadunku.
  <BooleanInput source="wygasle" label="Tylko po terminie ważności" defaultValue />,
  // Próg bezczynności jest konfiguracją słownika (869f1fzha); bez progów
  // ten filtr nie zwraca nic — API nie zgaduje wartości.
  <BooleanInput source="idle" label="Tylko bezczynne" defaultValue />,
  // Numer pojazdu spoza floty i bez przewoźnika — lista, do której prowadzi
  // kafelek pulpitu „Zlecenia z nieznanym pojazdem” (13.10.2026); ta sama
  // reguła co licznik (`unknownVehicleWhere` w API).
  <BooleanInput source="unknownVehicle" label="Tylko z nieznanym pojazdem" defaultValue />,
  // Giełda: „wystawione” (07.10.2026, prezentacja 02.10 pkt 9) to oferta
  // przyjęta przez giełdę — nowy klucz `postedOnExchange`. `naGieldzie`
  // (klucz bez zmian, react-admin go pamięta) liczy też oczekujące w kolejce,
  // więc jego etykieta mówi to wprost — wcześniej obie rzeczy nazywały się
  // tak samo. Nieudana próba nie liczy się do żadnego: zlecenie wciąż czeka
  // na człowieka. „Moje” bierze pod uwagę także błędy: to moja robota do
  // dokończenia.
  <BooleanInput source="postedOnExchange" label="Tylko wystawione na giełdzie" defaultValue />,
  <BooleanInput source="naGieldzie" label="Na giełdzie lub w kolejce" defaultValue />,
  <BooleanInput source="mojePublikacje" label="Tylko moje publikacje" defaultValue />,
  // Lista z etykietami jak w kolumnie, nie pole na kod (e2e, 28.09.2026).
  <SelectInput source="sourceSystem" label="Źródło" choices={SOURCE_CHOICES} />,
  <SelectInput source="tmsOrderStatus" label="Status w TMS" choices={TMS_ORDER_STATUS_CHOICES} />,
];

/**
 * Filtry bez „Tylko wystawione na giełdzie” (`postedOnExchange`) — jak na
 * produkcji 0.31.3, gdy przełącznik `postedOnExchangeFilter` jest wyłączony
 * (wersja „obecna”, decyzja użytkownika 12.10.2026): jeden filtr giełdy,
 * `naGieldzie` (liczy też kolejkę) z dawną etykietą. Reszta i kolejność bez
 * zmian. Wartość `postedOnExchange` zapamiętaną przez react-admina API wtedy
 * pomija (`filtersForFeatures`).
 */
export const LIST_FILTERS_WITHOUT_POSTED_ON_EXCHANGE = listFilters
  .filter((f) => f.props.source !== 'postedOnExchange')
  .map((f) =>
    f.props.source === 'naGieldzie'
      ? cloneElement(f, { label: 'Tylko wystawione na giełdzie' })
      : f,
  );

/**
 * Układ tabeli banku: nagłówki w jednej linii, komórki ciasno, a gdyby
 * mimo to tabela nie zmieściła się w oknie (wąski ekran, długi numer) —
 * przewija się sama tabela, nie cała strona z paskiem synchronizacji
 * i filtrami. Jedenaście kolumn zamiast czternastu (od 06.10.2026 z „VAT”, od
 * 07.10.2026 z „Giełdą”): treść dawnych kolumn siedzi w komórkach
 * z `listCells.tsx`.
 *
 * Decyzja użytkownika 08.10.2026: „Giełda” zostaje i komplet jedenastu
 * kolumn mieści się bez przewijania przy 1366 i 1440 px (z otwartym menu).
 * Dlatego komórka giełdy jest wąska (`ExchangeCell`: giełda z ikoną stanu,
 * stan słowem pod spodem), a pilnuje tego e2e (`checkListFits`, s1 i s4).
 */
// `width: 0` + `minWidth: 100%`: opakowanie nie zgłasza rodzicom szerokości
// tabeli (inaczej układ react-admina rośnie razem z nią i przewija się cała
// strona), a mimo to wypełnia dostępne miejsce.
const BANK_GRID_SX = {
  '& .RaDatagrid-tableWrapper': { overflowX: 'auto', width: 0, minWidth: '100%' },
  '& .RaDatagrid-headerCell': { whiteSpace: 'nowrap' },
  // Odstęp 6 px zamiast 8 px z każdej strony (08.10.2026) — przy jedenastu
  // kolumnach to ~45 px zapasu przy 1366 px.
  '& .MuiTableCell-root': { px: 0.75, py: 0.5, verticalAlign: 'middle' },
  '& .MuiTableCell-root:first-of-type': { pl: 2 },
  '& .column-fromDate': { whiteSpace: 'nowrap' },
} as const;

/**
 * Układ z produkcji 0.31.3 — dziesięć kolumn, odstęp 8 px — gdy przełącznik
 * `exchangeColumn` jest wyłączony (wersja „obecna”, decyzja użytkownika
 * 12.10.2026).
 */
const BANK_GRID_SX_WITHOUT_EXCHANGE = {
  ...BANK_GRID_SX,
  '& .MuiTableCell-root': { px: 1, py: 0.5, verticalAlign: 'middle' },
} as const;

/**
 * Z kolumną „€/km” (przełącznik `priceStatistics`) — odstęp 4 px z każdej
 * strony zamiast 6 px (poprawka po stagingu 14.10.2026). Przy dwunastu
 * kolumnach lista przy 1366 px przewijała się w bok o 21–38 px, gdy
 * w „Znacznikach” stał chip (decyzja użytkownika 08.10.2026: ma się
 * mieścić). Rachunek (szerokości tekstu zmierzone w Chromium, czcionka
 * Roboto/Helvetica/Arial): komórka „€/km” bez skrótu „rel.” — treść
 * ok. 33 px zamiast 55 px (−22 px), odstępy 11 kolumn po −4 px i pierwszej
 * −2 px (lewy zostaje 16 px) — −46 px; razem ok. −68 px, czyli ok. 30 px
 * zapasu wobec najgorszego zmierzonego przewijania (38 px).
 */
const PRICE_COLUMN_CELL_SX = {
  '& .MuiTableCell-root': { px: 0.5, py: 0.5, verticalAlign: 'middle' },
  '& .MuiTableCell-root:first-of-type': { pl: 2 },
} as const;

/**
 * Pola dokładane do filtrów listy z przełącznikiem `bankFtlFilters` (makieta
 * FTL pkt 9.3, 15.10.2026) — w „Dodaj filtr”; wiersz filtrów stałych zostaje,
 * jaki był (mieści się przy 1366 px), a domyślny widok roli pokazuje swoje
 * pola sam (`displayedFilters`). Klucze to kontrakt z API
 * (`user-offers/bank-ftl.ts`) i dane zapamiętywane przez react-admina —
 * napisem w `source`, jak w `listFilters`, bo strażnik kluczy
 * (`filters.test.ts`) czyta je z tego pliku i porównuje z API.
 */
const bankFtlFilters = [
  <SelectInput
    source="loadingWithinDays"
    label="Załadunek"
    choices={LOADING_WITHIN_CHOICES}
    defaultValue={7}
  />,
  <DateInput source="loadingDayFrom" label="Załadunek od dnia" />,
  <DateInput source="loadingDayTo" label="Załadunek do dnia" />,
  <SelectInput source="fromCountry" label="Kraj załadunku" choices={COUNTRY_CHOICES} />,
  <SelectInput source="toCountry" label="Kraj rozładunku" choices={COUNTRY_CHOICES} />,
  <SelectInput source="vehicleType" label="Typ pojazdu" choices={VEHICLE_TYPE_CHOICES} />,
  <SelectInput source="requirement" label="Wymagania" choices={REQUIREMENT_CHOICES} />,
  <CaretakerFilter source="vehicleCaretaker" label="Opiekun FTL" />,
  // `defaultValue`: dodany z „Dodaj filtr” od razu filtruje (jak inne przełączniki).
  <BooleanInput source="needsAssignment" label="Tylko wymagające obsadzenia" defaultValue />,
];

/**
 * Filtry z makiety FTL (pkt 9.3, przełącznik `bankFtlFilters`, 15.10.2026):
 * „Stan planowania” z wyborem kilku stanów (ten sam klucz), a na końcu nowe
 * pola z „Dodaj filtr” (`extra` — `bankFtlFilters`; `null` = przełącznik
 * wyłączony, filtry bez zmian). Opiekun FTL tylko dla planowania FTL — API
 * opiekunów odpowiada innym 403.
 */
export function withBankFtlFilters(
  filters: ReactElement[],
  extra: ReactElement[] | null,
  canPlan: boolean,
): ReactElement[] {
  if (!extra) return filters;
  const ownFilters = filters.map((f) =>
    (f.props as { source?: string }).source === 'planningStatus' ? (
      <PlanningStatusMultiFilter source="planningStatus" label="Stan planowania" alwaysOn />
    ) : (
      f
    ),
  );
  const added = extra.filter(
    (f) => canPlan || (f.props as { source?: string }).source !== CARETAKER_FILTER,
  );
  return [...ownFilters, ...added];
}

/**
 * „Realizacja wg TMS” (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 4a i 30;
 * przełącznik `executionDepartmentHint`) — w „Dodaj filtr”, żeby wiersz
 * filtrów stałych mieścił się przy 1366 px. Klucz `tmsExecution` to kontrakt
 * z API (`user-offers/filters.ts`) i dane zapamiętywane przez react-admina.
 * Działy rozstrzyga mapowanie wpisane przez administratora („Kto realizuje
 * wg TMS”) — bez mapowania na dany dział lista jest pusta.
 */
const executionFilters = [
  <SelectInput source="tmsExecution" label="Realizacja wg TMS" choices={EXECUTION_DEPARTMENT_CHOICES} />,
];

/**
 * Filtry, do których prowadzą pulpity FTL (makieta back office v2, A4,
 * 20.10.2026) — w „Dodaj filtr”, każdy za swoim przełącznikiem
 * (`ftlDashboardFilters.ts`). Klucze to kontrakt z API
 * (`user-offers/ftl-dashboard-filters.ts`); napisem w `source`, bo strażnik
 * kluczy (`filters.test.ts`) czyta je z tego pliku.
 */
/**
 * „Wniosek o odpięcie” — z wnioskami o zmianę pojazdu i przekazanie po
 * wyjeździe auta, gdy działa któryś z przełączników B6 (21.10.2026,
 * `ftlDashboardFilters.ts`). Haki w komponencie, jak przy „Znacznikach”.
 */
const UnassignRequestFilter = (props: {
  source: string;
  label: string;
  choices: typeof UNASSIGN_REQUEST_CHOICES;
}) => {
  const vehicleChangeOn = useFeature(FEATURE_VEHICLE_CHANGE_REQUEST);
  const tradeTransferOn = useFeature(FEATURE_TRADE_TRANSFER_BEFORE_DEPARTURE);
  return (
    <SelectInput
      {...props}
      choices={unassignRequestChoices(props.choices, vehicleChangeOn || tradeTransferOn)}
    />
  );
};

const ftlDashboardFilters = [
  <SelectInput source="myVariants" label="Moje warianty" choices={MY_VARIANTS_CHOICES} />,
  <BooleanInput source="myRegion" label="Tylko w moim regionie" defaultValue />,
  <UnassignRequestFilter source="unassignRequest" label="Wniosek o odpięcie" choices={UNASSIGN_REQUEST_CHOICES} />,
  <BooleanInput source="handedToTradeToday" label="Tylko przekazane dziś do Trade" defaultValue />,
  <BooleanInput source="loadingAlert" label="Tylko „jutro załadunek, bez auta”" defaultValue />,
];

/** Dokłada filtr „Realizacja wg TMS”; `null` = przełącznik wyłączony, filtry bez zmian. */
export function withExecutionFilter(filters: ReactElement[], extra: ReactElement[] | null): ReactElement[] {
  return extra ? [...filters, ...extra] : filters;
}

/**
 * „Etap prezlecenia” (makieta back office v2, BR-ADM-15, 20.10.2026;
 * przełącznik `preOrderStageInBank`) — w „Dodaj filtr”. Klucz
 * `preOrderStage` to kontrakt z API (`user-offers/filters.ts`) i dane
 * zapamiętywane przez react-admina.
 */
const preOrderStageFilters = [
  <SelectInput source="preOrderStage" label="Etap prezlecenia" choices={PRE_ORDER_STAGE_CHOICES} />,
];

/** To samo z nazwami „wysłane / zapisane w TMS” (przełącznik `tmsExportOneStep`, 21.10.2026). */
const preOrderStageFiltersTmsExport = [
  <SelectInput source="preOrderStage" label="Etap prezlecenia" choices={PRE_ORDER_STAGE_CHOICES_TMS_EXPORT} />,
];

/** Wybór powodu w filtrze: „Do Trade: …” / „Zwrot: …”, także zdjęte z listy. */
export function tradeReasonChoices(reasons: readonly TradeTransferReason[]) {
  return reasons.map((r) => ({
    id: r.code,
    name: `${isTradeTransferKind(r.kind) ? TRADE_TRANSFER_KIND_LABELS[r.kind] : r.kind}: ${r.label}${
      r.active ? '' : ' (zdjęty z listy)'
    }`,
  }));
}

/** Filtr „Powód przekazania/zwrotu” — lista ze słownika powodów (A6). */
const TradeReasonFilter = (props: { source: string; label: string }) => {
  const reasons = useTradeReasons(true);
  return <SelectInput {...props} choices={tradeReasonChoices(reasons ?? [])} />;
};

/**
 * Filtry działu Trade (makieta back office v2, A6 i A9, 20.10.2026) —
 * w „Dodaj filtr”, każdy za swoim przełącznikiem. Klucze to kontrakt z API
 * (`user-offers/trade-filters.ts`) i dane zapamiętywane przez react-admina;
 * pulpit Trade prowadzi do listy dokładnie tymi kluczami.
 */
const tradeFilters = [
  <TradeReasonFilter source="tradeReason" label="Powód przekazania/zwrotu" />,
  <BooleanInput source="carrierOffers" label="Z otwartą ofertą przewoźnika" defaultValue />,
  <BooleanInput source="returnedFromTrade" label="Tylko zwrócone z Trade" defaultValue />,
  <BooleanInput source="loadingAlert" label="Tylko „jutro załadunek, bez auta”" defaultValue />,
  // Paczka p. Mariusza 21.10, S-21.10-17: przewoźnik zaakceptowany, w TMS jeszcze go nie ma.
  <BooleanInput source="carrierAccepted" label="Przewoźnik zaakceptowany — czeka na TMS" defaultValue />,
];

/**
 * Klucz filtra → przełączniki, za którymi stoi (wszystkie muszą działać; jak
 * `TRADE_FILTER_FEATURES` w API). „Jutro załadunek” bez alertu przed
 * załadunkiem byłby zawsze pusty — więc też za `loadingTomorrowAlert`.
 */
export const TRADE_FILTER_FEATURES: Record<string, readonly string[]> = {
  tradeReason: [FEATURE_TRADE_TRANSFER_REASONS],
  carrierOffers: [FEATURE_CARRIER_OFFERS],
  returnedFromTrade: [FEATURE_TRADE_DASHBOARD],
  loadingAlert: [FEATURE_TRADE_DASHBOARD, FEATURE_LOADING_TOMORROW_ALERT],
  carrierAccepted: [FEATURE_CARRIER_OFFERS, FEATURE_CARRIER_ACCEPTANCE],
};

/** Dokłada filtry Trade, których przełącznik działa; bez żadnego — filtry bez zmian. */
export function withTradeFilters(
  filters: ReactElement[],
  extra: ReactElement[],
  enabled: (feature: string) => boolean,
): ReactElement[] {
  const shown = extra.filter((f) => {
    const features = TRADE_FILTER_FEATURES[(f.props as { source?: string }).source ?? ''];
    return features !== undefined && features.every(enabled);
  });
  return shown.length > 0 ? [...filters, ...shown] : filters;
}

/** Układ tabeli banku dla stanu przełączników listy. */
/**
 * Nagłówek „Typ pojazdu” (S-24.10-13) łamie się na dwie linie — reszta
 * nagłówków zostaje w jednej (`BANK_GRID_SX`), a ta kolumna nie musi być
 * szersza od swojej treści.
 */
export const RATE_TYPE_HEADER_CLASS = 'bank-header-wrap';

export function bankGridSx(exchangeColumn: boolean, priceStatistics: boolean, rateTypeColumns = false) {
  const base = exchangeColumn ? BANK_GRID_SX : BANK_GRID_SX_WITHOUT_EXCHANGE;
  // Dwie kolumny więcej (S-24.10-13) — odstępy jak przy „€/km”, żeby lista
  // mieściła się przy 1366 px.
  const tight = priceStatistics || rateTypeColumns ? { ...base, ...PRICE_COLUMN_CELL_SX } : base;
  // Pomiar e2e (24.10.2026, 1366 px, wszystkie przełączniki — 14 kolumn): przy
  // odstępie 4 px i relacji/kontrahencie 132/120 px tabela przewijała się
  // w bok o 56 px. Odstęp 2 px (−56 px) i relacja/kontrahent 116/100 px
  // (−36 px) dają ok. 35 px zapasu.
  return rateTypeColumns
    ? {
        ...tight,
        '& .MuiTableCell-root': { px: 0.25, py: 0.5, verticalAlign: 'middle' },
        '& .MuiTableCell-root:first-of-type': { pl: 2 },
        [`& .RaDatagrid-headerCell.${RATE_TYPE_HEADER_CLASS}`]: { whiteSpace: 'normal', lineHeight: 1.2 },
      }
    : tight;
}

/**
 * „Zlecenia mojej floty”: stały filtr „moje” (nie do zdjęcia), własne filtry
 * w magazynie — lista wszystkich zleceń ich nie dziedziczy — i tytuł z jej
 * pozycji menu (`MyFleetBankMenuItem`) zamiast tytułu listy wszystkich zleceń.
 */
const MY_FLEET_LIST_PROPS: Partial<Pick<ListProps, 'filter' | 'storeKey' | 'title'>> = {
  filter: { myOrders: true },
  storeKey: 'user-offers.myFleet',
  title: MY_FLEET_TITLE,
};

/**
 * `myFleet` — „Zlecenia mojej floty” (makieta FTL pkt 9.3, `MyFleetBank.tsx`):
 * ta sama tabela ze stałym filtrem „moje” i własnym magazynem filtrów.
 * `notice` — komunikat nad tabelą (np. brak wskazanego opiekuna).
 */
export const UserOfferList = ({
  myFleet = false,
  notice,
}: { myFleet?: boolean; notice?: ReactNode } = {}) => {
  // Przełączniki obszaru „lista zleceń i Trade” (decyzja użytkownika
  // 12.10.2026): bez nich lista jak na produkcji 0.31.3.
  const exchangeColumn = useFeature(FEATURE_EXCHANGE_COLUMN);
  const postedOnExchangeFilter = useFeature(FEATURE_POSTED_ON_EXCHANGE_FILTER);
  // Statystyka cen z VIA (13.10.2026) — kolumna „€/km” tylko z przełącznikiem.
  const priceStatistics = useFeature(FEATURE_PRICE_STATISTICS);
  // Etap 1 makiety FTL (15.10.2026): kolumny i filtry banku.
  const bankColumns = useFeature(FEATURE_BANK_COLUMNS);
  const bankFilters = useFeature(FEATURE_BANK_FILTERS);
  // Osobne kolumny „Stawka” i „Typ pojazdu” (S-24.10-13) — tylko w układzie z makiety.
  const rateTypeColumns = useFeature(FEATURE_BANK_RATE_TYPE_COLUMNS) && bankColumns;
  // Kto realizuje wg TMS (odpowiedzi 16.10.2026) — filtr tylko z przełącznikiem.
  const executionHint = useFeature(FEATURE_EXECUTION_HINT);
  // Etap prezlecenia (makieta back office v2, 20.10.2026) — filtr tylko z przełącznikiem.
  const preOrderStage = useFeature(FEATURE_PRE_ORDER_STAGE);
  const tmsExportNames = useFeature(FEATURE_TMS_EXPORT_ONE_STEP);
  // Filtry działu Trade z makiety back office v2 (20.10.2026).
  const tradeFlags: Record<string, boolean> = {
    [FEATURE_TRADE_TRANSFER_REASONS]: useFeature(FEATURE_TRADE_TRANSFER_REASONS),
    [FEATURE_CARRIER_OFFERS]: useFeature(FEATURE_CARRIER_OFFERS),
    [FEATURE_TRADE_DASHBOARD]: useFeature(FEATURE_TRADE_DASHBOARD),
    [FEATURE_LOADING_TOMORROW_ALERT]: useFeature(FEATURE_LOADING_TOMORROW_ALERT),
  };
  // Filtry pulpitów FTL (makieta back office v2, A4, 20.10.2026).
  const dashboardFlags: Record<string, boolean> = {
    [FEATURE_PLANNER_DASHBOARD]: useFeature(FEATURE_PLANNER_DASHBOARD),
    [FEATURE_TEAM_LEADER_DASHBOARD]: useFeature(FEATURE_TEAM_LEADER_DASHBOARD),
    [FEATURE_LOADING_TOMORROW_ALERT]: useFeature(FEATURE_LOADING_TOMORROW_ALERT),
  };
  const canPlan = useHasPermission('FTL_PLANNING');
  // Zanim przełączniki przyjdą — ładowanie, nie lista „obecna”, która po
  // chwili zamieniłaby się w „nową” (integracja przełączników 12.10.2026).
  const flagsLoading = useFeaturesLoading();
  if (flagsLoading) return <FeaturesLoading />;
  return (
    <List
      // Tablice filtrów wprost w wyrażeniu — strażnik kluczy (`filters.test.ts`)
      // czyta z niego nazwy tablic.
      filters={withFtlDashboardFilters(
        withTradeFilters(
          withExecutionFilter(
            withExecutionFilter(
              withBankFtlFilters(
                postedOnExchangeFilter ? listFilters : LIST_FILTERS_WITHOUT_POSTED_ON_EXCHANGE,
                bankFilters ? bankFtlFilters : null,
                canPlan,
              ),
              preOrderStage ? (tmsExportNames ? preOrderStageFiltersTmsExport : preOrderStageFilters) : null,
            ),
            executionHint ? executionFilters : null,
          ),
          tradeFilters,
          (feature) => tradeFlags[feature] === true,
        ),
        // „Jutro załadunek, bez auta” (`loadingAlert`) jest i w filtrach Trade,
        // i pulpitu Team Leadera — drugi raz nie wchodzi (`withFtlDashboardFilters`).
        ftlDashboardFilters,
        (feature) => dashboardFlags[feature] === true,
      )}
      title={menuLabel('user-offers')}
      // „Zlecenia mojej floty” nadpisuje zakres, magazyn i tytuł (`MY_FLEET_LIST_PROPS`).
      {...(myFleet ? MY_FLEET_LIST_PROPS : {})}
      filterDefaultValues={{ forklift: FORKLIFT_FILTER_DEFAULT }}
      // Eksport całości z polskimi nagłówkami i nazwami zamiast obiektów relacji.
      exporter={bankExporterFor(exchangeColumn, rateTypeColumns)}
      actions={<ListExportActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
    >
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        {/* Zlecenia z TMS przychodzą importem (W11): kiedy ostatnio i z jakim wynikiem. */}
        <SyncStatus />
        {notice}
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Opiekun FTL: zlecenia jego aut (omówienie 01.10.2026) — filtr, nie uprawnienie.
              Tylko z planowaniem FTL: traderowi API opiekunów odpowiada 403, a przełącznik
              zostałby wyszarzony (role działowe, 01.10.2026). W „Zleceniach mojej floty”
              „moje” jest stałe — przełącznika nie ma. */}
          {myFleet ? null : (
            <PermissionGate permission="FTL_PLANNING" element={ELEMENT.userOffersMyVehiclesToggle}>
              <MyOrdersToggle />
            </PermissionGate>
          )}
          {/* „Widoki”: widok domyślny roli i zapisane widoki (makieta FTL, 15.10.2026). */}
          <BankViewsMenu myFleet={myFleet} />
        </Stack>
        <Datagrid
          rowClick="show"
          bulkActionButtons={false}
          // Wiersz bezczynnego zlecenia jest wyróżniony tłem — flaga z API.
          rowSx={(r: RaRecord) =>
            r.idle ? { backgroundColor: 'rgba(237, 108, 2, 0.08)' } : {}
          }
          sx={bankGridSx(exchangeColumn, priceStatistics, rateTypeColumns)}
        >
          <FunctionField label="Nr" render={(r: RaRecord) => <NumberWithSourceCell record={r} />} />
          {/* Makieta FTL (15.10.2026, `bankFtlColumns`): pod relacją punkty,
              typ i wymagania; „Załadunek” to okno od–do. Bez nowych kolumn. */}
          {rateTypeColumns ? (
            <FunctionField label="Relacja" render={(r: RaRecord) => <RouteWithPointsCell record={r} />} />
          ) : bankColumns ? (
            <FunctionField label="Relacja" render={(r: RaRecord) => <RouteWithRequirementsCell record={r} />} />
          ) : (
            <FunctionField label="Relacja" render={(r: RaRecord) => <CompactRouteCell record={r} />} />
          )}
          {/* Dzień załadunku w Polsce, nie w strefie przeglądarki (`shared/dateTime.ts`). */}
          {bankColumns ? (
            <FunctionField
              source="fromDate"
              label="Załadunek"
              render={(r: RaRecord) => <LoadingWindowCell record={r} />}
            />
          ) : (
            <ClientDayField source="fromDate" label="Załadunek" />
          )}
          <FunctionField
            label="Stan planowania"
            render={(r: RaRecord) => <PlanningCell record={r} />}
          />
          {/* Zlecenie bez auta: warianty z autorami i „Obsadź…” (makieta FTL). */}
          {bankColumns ? (
            <FunctionField label="Pojazd" render={(r: RaRecord) => <VehicleOrVariantsCell record={r} />} />
          ) : (
            <FunctionField label="Pojazd" render={(r: RaRecord) => <CompactVehicleCell record={r} />} />
          )}
          <FunctionField label="TMS" render={(r: RaRecord) => <TmsCell record={r} />} />
          {/* Wymagany typ pojazdu we własnej kolumnie (S-24.10-13, odpowiedź z 21.10 na pytanie 20). */}
          {rateTypeColumns ? (
            <FunctionField
              label="Typ pojazdu"
              headerClassName={RATE_TYPE_HEADER_CLASS}
              render={(r: RaRecord) => <VehicleTypeCell record={r} />}
            />
          ) : null}
          {/* Stawka zlecenia pod kontrahentem (makieta FTL) — bez nowej kolumny;
              z `bankRateTypeColumns` we własnej kolumnie „Stawka”, sortowanej po kwocie. */}
          <FunctionField
            label="Kontrahent"
            render={(r: RaRecord) =>
              rateTypeColumns ? (
                <ContractorNarrowCell record={r} />
              ) : bankColumns ? (
                <ContractorWithRateCell record={r} />
              ) : (
                <CompactContractorCell record={r} />
              )
            }
          />
          {rateTypeColumns ? (
            <FunctionField
              source="price"
              sortBy="price"
              label="Stawka"
              textAlign="right"
              render={(r: RaRecord) => <RateCell record={r} />}
            />
          ) : null}
          <FunctionField label="Spółka" render={(r: RaRecord) => <CompanyCodeCell record={r} />} />
          {/* Rekomendowana stawka VAT — osobna kolumna (omówienie 01.10.2026). */}
          <FunctionField label="VAT" render={(r: RaRecord) => <VatCell record={r} />} />
          {/* Cena klienta za km na tle historii (statystyka cen z VIA,
              13.10.2026) — za przełącznikiem `priceStatistics`. */}
          {priceStatistics ? (
            <FunctionField label="€/km" render={(r: RaRecord) => <PricePerKmCell record={r} />} />
          ) : null}
          {/* Stan na giełdach z „od kiedy” — osobna kolumna od 07.10.2026
              (prezentacja 02.10, pkt 9), wcześniej jeden ze „Znaczników”. Dla
              każdego zalogowanego, bez przycisków giełd. */}
          {exchangeColumn ? (
            <FunctionField label="Giełda" render={(r: RaRecord) => <ExchangeCell record={r} />} />
          ) : null}
          <FunctionField label="Znaczniki" render={(r: RaRecord) => <FlagsCell record={r} />} />
        </Datagrid>
      </Stack>
    </List>
  );
};
